import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { User as UserIcon } from 'feather-icons-react';
import { useAuth0 } from '../../auth/authCompat';
import {
  actions as getUserGetActions,
  getUserGetState,
} from '../../ducks/user/get';
import useTwitchUsernameFromAuth0 from '../../hooks/useTwitchUsernameFromAuth0';
import Troutcoin from '../Troutcoin';

const PENDING_PAYPAL_CREDIT_KEY = 'rng_pending_paypal_credit';
const PENDING_PAYPAL_EVENT = 'rng:pending-paypal-credit';
const PENDING_PAYPAL_MAX_AGE_MS = 10 * 60 * 1000;
const PAYPAL_POLL_DELAYS_MS = [0, 3000, 5000, 8000, 13000, 21000, 34000, 55000, 89000, 144000];

const NavBarProfile = () => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const twitchUsername = useTwitchUsernameFromAuth0();
  const displayName = user?.name || twitchUsername;
  const dispatch = useDispatch();

  const userData = useSelector((state) => getUserGetState(state)?.data);
  const troutCoins = userData?.troutCoins;

  const pollRef = useRef({
    isPolling: false,
    baselineCoins: null,
    attempt: 0,
    timeoutId: null,
  });

  const clearPendingMarker = useCallback(() => {
    try {
      window.sessionStorage?.removeItem(PENDING_PAYPAL_CREDIT_KEY);
    } catch (e) {
      // ignore
    }
  }, []);

  const readPendingMarker = useCallback(() => {
    try {
      const raw = window.sessionStorage?.getItem(PENDING_PAYPAL_CREDIT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const ts = Number(parsed?.ts);
      const baselineCoins = Number(parsed?.baselineCoins);
      if (!Number.isFinite(ts) || !Number.isFinite(baselineCoins)) return null;
      if (Date.now() - ts > PENDING_PAYPAL_MAX_AGE_MS) return null;
      return { ts, baselineCoins };
    } catch (e) {
      return null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    const st = pollRef.current;
    st.isPolling = false;
    st.attempt = 0;
    st.baselineCoins = null;
    if (st.timeoutId) clearTimeout(st.timeoutId);
    st.timeoutId = null;
    clearPendingMarker();
  }, [clearPendingMarker]);

  const startPollingIfNeeded = useCallback(() => {
    const marker = readPendingMarker();
    if (!marker) return;
    const st = pollRef.current;
    if (st.isPolling) return;

    st.isPolling = true;
    st.baselineCoins = marker.baselineCoins;
    st.attempt = 0;

    const tick = async () => {
      const s = pollRef.current;
      if (!s.isPolling) return;

      try {
        await dispatch(getUserGetActions.load({ silent: true }, getAccessTokenSilently));
      } catch (e) {
        // ignore transient refresh failures
      }

      s.attempt += 1;
      if (s.attempt >= PAYPAL_POLL_DELAYS_MS.length) {
        stopPolling();
        return;
      }
      s.timeoutId = setTimeout(tick, PAYPAL_POLL_DELAYS_MS[s.attempt]);
    };

    st.timeoutId = setTimeout(tick, PAYPAL_POLL_DELAYS_MS[0]);
  }, [dispatch, getAccessTokenSilently, readPendingMarker, stopPolling]);

  useEffect(() => {
    if (!userData) {
      // Load canonical user (server resolves linked identities)
      dispatch(getUserGetActions.load({}, getAccessTokenSilently));
    }
  }, [dispatch, userData, getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated) return;
    startPollingIfNeeded();
  }, [isAuthenticated, startPollingIfNeeded]);

  useEffect(() => {
    const handler = () => startPollingIfNeeded();
    if (isAuthenticated) {
      window.addEventListener(PENDING_PAYPAL_EVENT, handler);
    }
    return () => window.removeEventListener(PENDING_PAYPAL_EVENT, handler);
  }, [isAuthenticated, startPollingIfNeeded]);

  useEffect(() => {
    const st = pollRef.current;
    if (!st.isPolling) return;
    if (typeof st.baselineCoins !== 'number') return;
    if (typeof troutCoins !== 'number') return;
    if (troutCoins > st.baselineCoins) {
      stopPolling();
    }
  }, [troutCoins, stopPolling]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="nav-bar__profile">
      <Link
        to="/profile"
        className="nav-bar__profile__profile-link"
        title="Profile"
      >
        <UserIcon />
        <span className="nav-bar__profile__profile-link__text">{displayName}</span>
      </Link>
      <div className="nav-bar__profile__trout-coins">
        <div className="nav-bar__profile__trout-coins__icon">
          <Troutcoin size={16} />
        </div>
        <div className="nav-bar__profile__trout-coins__count">
          {userData?.troutCoins || 0}
        </div>
      </div>
    </div>
  );
};

export default NavBarProfile;
